import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import PDFViewer from '../components/PDFViewer';
import { usePermission } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { ContentsAPI, UploadsAPI, UploadGroupsAPI, S3API } from '../api/api';
import {
    ChevronRight,
    ChevronLeft,
    X,
    Download,
    Maximize2,
    Minimize2,
    FolderOpen,
    Image,
    Gift,
    DollarSign,
    Percent,
    Trophy,
    Medal,
    Crown,
    Coins,
    CreditCard,
    PiggyBank,
    TrendingUp,
    FileText,
    Video
} from 'lucide-react';
import { LUCIDE_ICON_MAP } from '../components/LucideIconPicker';
import './VisionPage.css';

// 제목 기반 아이콘 매핑 함수
const getIconByTitle = (title, size = 48) => {
    const lowerTitle = title?.toLowerCase() || '';

    if (lowerTitle.includes('인센티브') || lowerTitle.includes('incentive')) {
        return <Coins size={size} />;
    }
    if (lowerTitle.includes('급여') || lowerTitle.includes('월급') || lowerTitle.includes('salary')) {
        return <DollarSign size={size} />;
    }
    if (lowerTitle.includes('보너스') || lowerTitle.includes('bonus')) {
        return <Gift size={size} />;
    }
    if (lowerTitle.includes('수당') || lowerTitle.includes('수수료')) {
        return <CreditCard size={size} />;
    }
    if (lowerTitle.includes('복리') || lowerTitle.includes('복지')) {
        return <PiggyBank size={size} />;
    }
    if (lowerTitle.includes('승진') || lowerTitle.includes('진급')) {
        return <Crown size={size} />;
    }
    if (lowerTitle.includes('포상') || lowerTitle.includes('상금')) {
        return <Trophy size={size} />;
    }
    if (lowerTitle.includes('수상') || lowerTitle.includes('시상')) {
        return <Medal size={size} />;
    }
    if (lowerTitle.includes('성과') || lowerTitle.includes('실적')) {
        return <TrendingUp size={size} />;
    }
    if (lowerTitle.includes('할인') || lowerTitle.includes('%')) {
        return <Percent size={size} />;
    }

    return <Gift size={size} />;
};

// DB 아이콘 우선, 폴백으로 제목 기반 매핑
const getItemIcon = (item, size = 48) => {
    const LucideIcon = LUCIDE_ICON_MAP[item.icon];
    if (LucideIcon) return <LucideIcon size={size} />;
    return getIconByTitle(item.title, size);
};

// 제목 기반 설명 생성 함수
const getDescriptionByTitle = (title) => {
    const lowerTitle = title?.toLowerCase() || '';

    if (lowerTitle.includes('인센티브')) {
        return '성과에 따른 인센티브 제도를 안내합니다';
    }
    if (lowerTitle.includes('급여') || lowerTitle.includes('월급')) {
        return '급여 체계와 지급 기준을 확인합니다';
    }
    if (lowerTitle.includes('보너스')) {
        return '보너스 지급 조건과 산정 방식을 안내합니다';
    }
    if (lowerTitle.includes('수당')) {
        return '각종 수당 및 지원금 정보를 확인합니다';
    }
    if (lowerTitle.includes('복리') || lowerTitle.includes('복지')) {
        return '직원 복리후생 제도를 소개합니다';
    }
    if (lowerTitle.includes('승진')) {
        return '승진 기준과 경력 개발 경로를 안내합니다';
    }
    if (lowerTitle.includes('포상')) {
        return '우수 성과자 포상 제도를 확인합니다';
    }

    return '성과에 따른 보상 체계를 안내합니다';
};

// 제목 기반 뱃지 생성 함수
const getBadgesByTitle = (title) => {
    const lowerTitle = title?.toLowerCase() || '';

    // 수수료
    if (lowerTitle.includes('수수료') || lowerTitle.includes('commission')) {
        return [{ label: 'Commission', primary: true }, { label: '수수료' }, { label: '정산' }];
    }
    // Top Of Meta
    if (lowerTitle.includes('top of meta') || lowerTitle.includes('top')) {
        return [{ label: 'Award', primary: true }, { label: 'TOP' }, { label: '성과' }];
    }
    // 해외여행 프로모션 사진
    if (lowerTitle.includes('해외여행 프로모션 사진') || lowerTitle.includes('프로모션 사진')) {
        return [{ label: 'Gallery', primary: true }, { label: '해외여행' }, { label: '추억' }];
    }
    // 해외 여행 프로모션
    if (lowerTitle.includes('해외 여행 프로모션') || lowerTitle.includes('해외여행 프로모션')) {
        return [{ label: 'Promotion', primary: true }, { label: '해외여행' }, { label: '인센티브' }];
    }
    // 복지
    if (lowerTitle.includes('복리') || lowerTitle.includes('복지')) {
        return [{ label: 'Welfare', primary: true }, { label: '복지' }, { label: '혜택' }];
    }

    return [{ label: 'Reward', primary: true }, { label: '보상' }, { label: '혜택' }];
};

function RewardPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { canAccessSection, getCategoriesBySection, getPartsByCategory, isLoading: permissionLoading } = usePermission();
    const { info, error: showError } = useAlert();

    const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

    const [sections, setSections] = useState([]);
    const [dataLoading, setDataLoading] = useState(true);
    const [dataError, setDataError] = useState(null);
    // 활성 카테고리 (단일 선택 필터)
    const [activeCategory, setActiveCategory] = useState(null);

    useEffect(() => {
        if (!permissionLoading && !isAdmin && !canAccessSection('보상')) {
            navigate('/main');
        }
    }, [permissionLoading, isAdmin, canAccessSection, navigate]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentImages, setCurrentImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [modalTitle, setModalTitle] = useState('');
    const [isFullscreen, setIsFullscreen] = useState(false);

    const [modalView, setModalView] = useState('closed');
    const [selectedPart, setSelectedPart] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [uploadGroups, setUploadGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(false);

    // PDF 지원을 위한 미디어 타입 상태
    const [mediaItems, setMediaItems] = useState([]);
    const [currentMediaType, setCurrentMediaType] = useState('image');

    // 썸네일 상태
    const [thumbnailUrls, setThumbnailUrls] = useState({});

    // 섹션 진입 시 모든 썸네일을 일괄 로딩하는 함수
    const loadAllThumbnails = useCallback(async (allItems) => {
        if (allItems.length === 0) return;

        const S3_URL_PATTERN = /^https:\/\/.*\.s3\..*\.amazonaws\.com\/(.+)$/;
        const batchItems = [];
        const immediateUrls = {};

        const needsGroupLookup = allItems.filter(item => !item.thumbnailUrl);
        const groupResults = await Promise.allSettled(
            needsGroupLookup.map(async (item) => {
                const res = await UploadGroupsAPI.getByPartId(item.partId, { silent: true });
                const groups = res.data?.groups || res.groups || [];
                return { partId: item.partId, groups };
            })
        );

        for (const item of allItems) {
            if (item.thumbnailUrl) {
                const match = item.thumbnailUrl.match(S3_URL_PATTERN);
                if (match) {
                    batchItems.push({ id: String(item.partId), type: 'S3_KEY', value: decodeURIComponent(match[1]) });
                } else {
                    immediateUrls[item.partId] = item.thumbnailUrl;
                }
            }
        }

        for (const result of groupResults) {
            if (result.status !== 'fulfilled') continue;
            const { partId, groups } = result.value;
            if (groups.length === 0) { immediateUrls[partId] = null; continue; }

            const group = groups[0];
            if (group.thumbnailS3Key) {
                batchItems.push({ id: String(partId), type: 'S3_KEY', value: group.thumbnailS3Key });
            } else if (group.thumbnailFileType === 'VIDEO') {
                immediateUrls[partId] = 'VIDEO_ICON';
            } else if (group.thumbnailUploadId) {
                batchItems.push({ id: String(partId), type: 'UPLOAD_ID', value: String(group.thumbnailUploadId), urlType: 'VIEW' });
            } else {
                immediateUrls[partId] = null;
            }
        }

        if (Object.keys(immediateUrls).length > 0) {
            setThumbnailUrls(prev => ({ ...prev, ...immediateUrls }));
        }

        if (batchItems.length > 0) {
            try {
                const batchRes = await S3API.batchPresign(batchItems);
                const results = batchRes.data?.results || batchRes.results || {};
                const urlMap = {};
                for (const [partId, data] of Object.entries(results)) {
                    urlMap[partId] = data ? data.url : null;
                }
                setThumbnailUrls(prev => ({ ...prev, ...urlMap }));
            } catch (error) {
                console.error('Failed to batch load thumbnails:', error);
                const failedMap = {};
                batchItems.forEach(item => { failedMap[item.id] = null; });
                setThumbnailUrls(prev => ({ ...prev, ...failedMap }));
            }
        }
    }, []);

    // 카테고리 선택 (단일 선택)
    const selectCategory = (category) => {
        setActiveCategory(category);
    };

    // 전체보기
    const showAll = () => {
        setActiveCategory(null);
    };

    // DB에서 보상 데이터 로드
    useEffect(() => {
        const loadRewardData = async () => {
            try {
                setDataLoading(true);
                setDataError(null);

                const menuResponse = await ContentsAPI.getMenu({ silent: true });
                const menuData = menuResponse.data || menuResponse;

                const rewardSection = menuData.sections?.find(s => s.name === '보상');
                if (!rewardSection) {
                    setSections([]);
                    return;
                }

                // 카테고리를 파트 수에 따라 분류
                // 파트 1개: 카테고리명이 카드 제목 (기존 방식)
                // 파트 2개+: 카테고리가 섹션 헤더, 파트별 카드 (비전 방식)
                const singlePartItems = [];
                const multiPartSections = [];

                (rewardSection.categories || []).forEach((category) => {
                    const parts = category.parts || [];

                    if (parts.length <= 1) {
                        parts.forEach((part) => {
                            singlePartItems.push({
                                id: part.id,
                                partId: part.id,
                                title: category.name,
                                partTitle: part.name,
                                icon: part.icon || '📄',
                                buttonText: part.buttonText || '바로가기',
                                url: part.partType === 'LINK' ? part.externalUrl : null,
                                hasGroups: part.partType === 'GALLERY' || part.partType === 'MONTHLY_TABS',
                                partType: part.partType,
                                categoryName: category.name,
                                thumbnailUrl: part.thumbnailUrl || null,
                            });
                        });
                    } else {
                        const items = parts.map((part) => ({
                            id: part.id,
                            partId: part.id,
                            title: part.name,
                            icon: part.icon || '📄',
                            buttonText: part.buttonText || '바로가기',
                            url: part.partType === 'LINK' ? part.externalUrl : null,
                            hasGroups: part.partType === 'GALLERY' || part.partType === 'MONTHLY_TABS',
                            partType: part.partType,
                            categoryName: category.name,
                            thumbnailUrl: part.thumbnailUrl || null,
                        }));

                        multiPartSections.push({
                            id: category.id,
                            title: category.name,
                            icon: category.icon || '📄',
                            count: items.length,
                            items,
                        });
                    }
                });

                const transformedSections = [];
                if (singlePartItems.length > 0) {
                    transformedSections.push({
                        id: 'reward-unified',
                        title: '보상',
                        icon: '🏆',
                        count: singlePartItems.length,
                        items: singlePartItems,
                    });
                }
                transformedSections.push(...multiPartSections);

                setSections(transformedSections);

                // 모든 썸네일 일괄 로딩 (silent - 로딩 스피너 미표시)
                const allParts = transformedSections.flatMap(section =>
                    section.items.map(item => ({
                        partId: item.partId,
                        thumbnailUrl: item.thumbnailUrl,
                    }))
                );
                loadAllThumbnails(allParts);
            } catch (error) {
                console.error('Failed to load reward data:', error);
                setDataError(error.message || '데이터를 불러오는데 실패했습니다.');
            } finally {
                setDataLoading(false);
            }
        };

        if (!permissionLoading && (isAdmin || canAccessSection('보상'))) {
            loadRewardData();
        }
    }, [permissionLoading, isAdmin, canAccessSection]);

    // 권한에 따라 필터링된 섹션
    const filteredSections = useMemo(() => {
        if (isAdmin) {
            return sections;
        }

        const accessibleCategories = getCategoriesBySection('보상');
        const accessibleCategoryNames = accessibleCategories.map(c => c.name);

        return sections.map(section => {
            // 각 아이템의 카테고리 접근 권한 확인
            const filteredItems = section.items.filter(item => {
                if (!accessibleCategoryNames.includes(item.categoryName)) {
                    return false;
                }
                const accessibleParts = getPartsByCategory(item.categoryName);
                const accessiblePartNames = accessibleParts.map(p => p.name);
                return accessiblePartNames.includes(item.partTitle || item.title);
            });

            return {
                ...section,
                items: filteredItems,
                count: filteredItems.length
            };
        }).filter(section => section.items.length > 0);
    }, [isAdmin, sections, getCategoriesBySection, getPartsByCategory]);

    // 카테고리 목록 추출 (필터용)
    const categories = useMemo(() => {
        const allCategoryNames = filteredSections.flatMap(section =>
            [...new Set(section.items.map(item => item.categoryName))]
        );
        return [...new Set(allCategoryNames)].sort();
    }, [filteredSections]);

    // 필터링된 섹션 (단일 카테고리 선택)
    const displayedSections = useMemo(() => {
        if (activeCategory === null) {
            return filteredSections;
        }
        return filteredSections.map(section => {
            if (section.title === activeCategory) return section;
            const filtered = section.items.filter(item => item.categoryName === activeCategory);
            return { ...section, items: filtered, count: filtered.length };
        }).filter(section => section.items.length > 0);
    }, [filteredSections, activeCategory]);

    const handleCardClick = async (card) => {
        // URL이 있으면 외부 링크로 이동
        if (card.url) {
            window.open(card.url, '_blank', 'noopener,noreferrer');
            return;
        }

        // partId가 있으면 그룹 모달 시도 (hasGroups 여부와 관계없이)
        if (card.partId) {
            setSelectedPart(card);
            setModalTitle(card.title);
            setModalView('groups');
            document.body.style.overflow = 'hidden';
            await loadUploadGroups(card.partId);
            return;
        }

        info('이 항목에는 연결된 콘텐츠가 없습니다.');
    };

    const loadUploadGroups = async (partId) => {
        setGroupsLoading(true);
        try {
            const response = await UploadGroupsAPI.getByPartId(partId);
            setUploadGroups(response.data?.groups || response.groups || []);
        } catch (error) {
            console.error('Failed to load upload groups:', error);
            setUploadGroups([]);
        } finally {
            setGroupsLoading(false);
        }
    };

    const handleGroupSelect = async (group) => {
        setSelectedGroup(group);
        setGroupsLoading(true);
        try {
            const response = await UploadsAPI.getUploadsByGroupId(group.id, { limit: 1000 });
            const uploads = response.data?.uploads || response.uploads || [];

            // URL을 병렬로 요청
            const urlResults = await Promise.all(
                uploads.map(async (upload) => {
                    try {
                        const res = await S3API.getDownloadUrl(upload.id, 'VIEW');
                        return res.data?.url || res.url;
                    } catch (e) {
                        console.error('Failed to get presigned URL:', e);
                        return null;
                    }
                })
            );

            // 결과를 동기적으로 빌드 (순서 보장)
            const items = [];
            const urls = [];
            uploads.forEach((upload, index) => {
                const url = urlResults[index];
                if (url) {
                    const isPDF = upload.mimeType === 'application/pdf' ||
                                  upload.fileName?.toLowerCase().endsWith('.pdf');
                    const isVideo = upload.fileType === 'VIDEO' || upload.mimeType?.startsWith('video/');
                    items.push({
                        type: isPDF ? 'pdf' : isVideo ? 'video' : 'image',
                        src: url,
                        fileName: upload.originalName || upload.fileName
                    });
                    urls.push(url);
                }
            });

            setMediaItems(items);
            setCurrentImages(urls);
            setCurrentImageIndex(0);
            setCurrentMediaType(items[0]?.type || 'image');
            setModalView('files');
            setIsModalOpen(true);
        } catch (err) {
            console.error('Failed to load group files:', err);
            showError('파일을 불러오는데 실패했습니다.');
        } finally {
            setGroupsLoading(false);
        }
    };

    const handleBackToGroups = () => {
        setSelectedGroup(null);
        setCurrentImages([]);
        setMediaItems([]);
        setCurrentImageIndex(0);
        setCurrentMediaType('image');
        setModalView('groups');
        setIsModalOpen(false);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentImages([]);
        setMediaItems([]);
        setCurrentImageIndex(0);
        setCurrentMediaType('image');
        setModalTitle('');
        setIsFullscreen(false);
        setModalView('closed');
        setSelectedPart(null);
        setSelectedGroup(null);
        setUploadGroups([]);
        document.body.style.overflow = 'auto';
    };

    const nextImage = () => {
        const newIndex = (currentImageIndex + 1) % currentImages.length;
        setCurrentImageIndex(newIndex);
        if (mediaItems.length > 0) {
            setCurrentMediaType(mediaItems[newIndex]?.type || 'image');
        }
    };

    const prevImage = () => {
        const newIndex = (currentImageIndex - 1 + currentImages.length) % currentImages.length;
        setCurrentImageIndex(newIndex);
        if (mediaItems.length > 0) {
            setCurrentMediaType(mediaItems[newIndex]?.type || 'image');
        }
    };

    const downloadCurrentImage = () => {
        const imageUrl = currentImages[currentImageIndex];
        const fileName = `${modalTitle}_${currentImageIndex + 1}.png`;
        fetch(imageUrl)
            .then((response) => response.blob())
            .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            })
            .catch((err) => {
                console.error('다운로드 실패:', err);
                showError('다운로드에 실패했습니다.');
            });
    };

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') closeModal();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    useEffect(() => {
        if (isModalOpen && currentImageIndex >= 0) {
            const thumbnailElement = document.querySelector(`.viewer-thumbnail:nth-child(${currentImageIndex + 1})`);
            if (thumbnailElement) {
                thumbnailElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center',
                });
            }
        }
    }, [currentImageIndex, isModalOpen]);

    const isLoading = permissionLoading || dataLoading;

    if (isLoading) {
        return (
            <div className="vision-page">
                <Navigation />
                <div className="vision-loading">
                    <div className="loading-spinner"></div>
                    <p>로딩 중...</p>
                </div>
            </div>
        );
    }

    if (dataError) {
        return (
            <div className="vision-page">
                <Navigation />
                <div className="vision-error">
                    <p className="error-title">오류가 발생했습니다</p>
                    <p className="error-message">{dataError}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="vision-page">
            <Navigation />

            <div className="vision-container">
                {/* 페이지 헤더 */}
                <header className="vision-header">
                    <h1 className="vision-title">보상 <span className="vision-title-en">Reward</span></h1>
                    <p className="vision-description">
                        성과에 따른 보상 체계를 관리합니다
                    </p>
                </header>

                <div className="vision-layout">
                    {/* 메인 콘텐츠 영역 */}
                    <main className="vision-main">
                        {displayedSections.length === 0 ? (
                            <div className="vision-empty">
                                <p>접근 가능한 항목이 없습니다.</p>
                                <p className="empty-hint">관리자에게 권한을 요청해주세요.</p>
                            </div>
                        ) : (
                            <div className="category-list">
                                {displayedSections.map((section) => (
                                    <div key={section.id} className="category-section">
                                        <div className="category-header">
                                            <h2 className="category-title">{section.title}</h2>
                                            <span className="category-count">{section.items.length}</span>
                                        </div>

                                        <div className="category-items">
                                            {section.items.map((item) => (
                                                <article
                                                    key={item.id}
                                                    className="article-card"
                                                    onClick={() => handleCardClick(item)}
                                                >
                                                    <div className="article-content">
                                                        <div className="article-badges">
                                                            {getBadgesByTitle(item.title).map((badge, idx) => (
                                                                <span
                                                                    key={idx}
                                                                    className={`article-badge ${badge.primary ? '' : 'secondary'}`}
                                                                >
                                                                    {badge.label}
                                                                </span>
                                                            ))}
                                                        </div>
                                                        <h3 className="article-title">{item.title}</h3>
                                                        <p className="article-description">{getDescriptionByTitle(item.title)}</p>
                                                    </div>
                                                    <div className="article-thumbnail">
                                                        {thumbnailUrls[item.partId] === undefined ? (
                                                            <div className="thumbnail-skeleton"></div>
                                                        ) : thumbnailUrls[item.partId] === 'VIDEO_ICON' ? (
                                                            <div className="thumbnail-fallback video-thumbnail">
                                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                                    <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" />
                                                                </svg>
                                                            </div>
                                                        ) : thumbnailUrls[item.partId] ? (
                                                            <>
                                                                <img
                                                                    src={thumbnailUrls[item.partId]}
                                                                    alt={item.title}
                                                                    className="thumbnail-image"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        if (e.target.nextSibling) {
                                                                            e.target.nextSibling.style.display = 'flex';
                                                                        }
                                                                    }}
                                                                />
                                                                <div className="thumbnail-fallback" style={{ display: 'none' }}>
                                                                    {getItemIcon(item, 36)}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="thumbnail-fallback">
                                                                {getItemIcon(item, 36)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </main>

                    {/* 사이드바 - 카테고리 필터 */}
                    {categories.length > 0 && (
                        <aside className="vision-sidebar">
                            <div className="sidebar-card">
                                <div className="sidebar-header">
                                    <span className="sidebar-title">카테고리</span>
                                </div>
                                <div className="sidebar-nav">
                                    <button
                                        className={`nav-item ${activeCategory === null ? 'active' : ''}`}
                                        onClick={showAll}
                                    >
                                        전체보기
                                    </button>
                                    {categories.map((category) => (
                                        <button
                                            key={category}
                                            className={`nav-item ${activeCategory === category ? 'active' : ''}`}
                                            onClick={() => selectCategory(category)}
                                        >
                                            {category}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </aside>
                    )}
                </div>
            </div>

            {/* 그룹 선택 모달 */}
            {modalView === 'groups' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="group-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="group-modal-header">
                            <div className="group-modal-title-section">
                                <h2 className="group-modal-title">{modalTitle}</h2>
                                <span className="group-modal-count">{uploadGroups.length}개 자료</span>
                            </div>
                            <button className="modal-close-btn" onClick={closeModal}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="group-modal-body">
                            {groupsLoading ? (
                                <div className="groups-loading">
                                    <div className="loading-spinner small"></div>
                                    <span>자료 목록을 불러오는 중...</span>
                                </div>
                            ) : uploadGroups.length === 0 ? (
                                <div className="groups-empty">
                                    <FolderOpen size={48} strokeWidth={1} />
                                    <span>등록된 자료가 없습니다.</span>
                                </div>
                            ) : (
                                <div className="groups-list">
                                    {uploadGroups.map((group) => (
                                        <div
                                            key={group.id}
                                            className="group-item"
                                            onClick={() => handleGroupSelect(group)}
                                        >
                                            <div className="group-item-icon">
                                                <FolderOpen size={24} />
                                            </div>
                                            <div className="group-item-info">
                                                <span className="group-item-title">{group.title}</span>
                                                <span className="group-item-count">{group.fileCount || 0}개 파일</span>
                                            </div>
                                            <ChevronRight size={20} className="group-item-arrow" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* 이미지/PDF 뷰어 모달 */}
            {isModalOpen && modalView === 'files' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <button
                        className="viewer-nav-btn prev"
                        onClick={(e) => { e.stopPropagation(); prevImage(); }}
                        disabled={currentImages.length <= 1}
                    >
                        <ChevronLeft size={24} />
                    </button>

                    <div
                        className={`viewer-modal ${isFullscreen ? 'fullscreen' : ''} ${currentMediaType === 'pdf' && mediaItems.length <= 1 ? 'pdf-layout' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="viewer-header">
                            <div className="viewer-header-left">
                                <button className="viewer-back-btn" onClick={handleBackToGroups}>
                                    <ChevronLeft size={20} />
                                    <span>목록</span>
                                </button>
                                <h2 className="viewer-title">{selectedGroup?.title || modalTitle}</h2>
                            </div>
                            <div className="viewer-header-right">
                                <span className="viewer-counter">
                                    {currentImageIndex + 1} / {currentImages.length}
                                </span>
                                {currentMediaType !== 'pdf' && (
                                    <>
                                        <button className="viewer-action-btn" onClick={downloadCurrentImage} title="다운로드">
                                            <Download size={20} />
                                        </button>
                                        <button
                                            className="viewer-action-btn"
                                            onClick={() => setIsFullscreen(!isFullscreen)}
                                            title={isFullscreen ? '축소' : '전체화면'}
                                        >
                                            {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                                        </button>
                                    </>
                                )}
                                <button
                                    className="viewer-close-btn"
                                    onClick={isFullscreen ? () => setIsFullscreen(false) : closeModal}
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        <div className="viewer-body">
                            {isFullscreen && (
                                <button
                                    className="fullscreen-close-btn"
                                    onClick={() => setIsFullscreen(false)}
                                    title="축소"
                                >
                                    <X size={28} />
                                </button>
                            )}

                            <div className="viewer-image-container">
                                {currentImages.length > 0 ? (
                                    currentMediaType === 'pdf' ? (
                                        <PDFViewer
                                            fileUrl={currentImages[currentImageIndex]}
                                            fileName={mediaItems[currentImageIndex]?.fileName}
                                        />
                                    ) : currentMediaType === 'video' ? (
                                        <video
                                            key={currentImages[currentImageIndex]}
                                            src={currentImages[currentImageIndex]}
                                            controls
                                            autoPlay
                                            className="viewer-video"
                                        >
                                            Your browser does not support the video tag.
                                        </video>
                                    ) : (
                                        <img
                                            src={currentImages[currentImageIndex]}
                                            alt={`${modalTitle} - ${currentImageIndex + 1}`}
                                            className="viewer-image"
                                        />
                                    )
                                ) : (
                                    <div className="no-images">
                                        <Image size={48} strokeWidth={1} />
                                        <span>이미지가 없습니다</span>
                                    </div>
                                )}
                            </div>

                        </div>

                        <div className="viewer-thumbnails">
                            {currentImages.map((src, index) => {
                                const mediaType = mediaItems[index]?.type;
                                return (
                                    <div
                                        key={index}
                                        className={`viewer-thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                        onClick={() => {
                                            setCurrentImageIndex(index);
                                            if (mediaItems.length > 0) {
                                                setCurrentMediaType(mediaItems[index]?.type || 'image');
                                            }
                                        }}
                                    >
                                        {mediaType === 'pdf' ? (
                                            <FileText size={18} />
                                        ) : mediaType === 'video' ? (
                                            <Video size={18} />
                                        ) : (
                                            <Image size={18} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <button
                        className="viewer-nav-btn next"
                        onClick={(e) => { e.stopPropagation(); nextImage(); }}
                        disabled={currentImages.length <= 1}
                    >
                        <ChevronRight size={24} />
                    </button>
                </div>
            )}

            {/* 페이지 푸터 */}
            <footer className="vision-footer">
                <div className="footer-disclaimer">
                    <strong>본 CIS 자료는 MAP Group의 지식재산</strong>이며, 무단 복제·배포 시<br />
                    <span className="footer-highlight">비밀유지서약에 따른 법적 책임</span>을 부담해야 합니다.
                </div>
            </footer>
        </div>
    );
}

export default RewardPage;
